import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAccountWallet, useB3Config, useModalStore, useProfile, useSimBalance, useUser } from "../../../../global-account/react/index.js";
import { validateImageUrl } from "../../../../global-account/react/utils/profileDisplay.js";
import { AVATAR_COLORS } from "../../../../shared/constants/index.js";
import { formatUsername } from "../../../../shared/utils/index.js";
import { formatDisplayNumber } from "../../../../shared/utils/number.js";
import Avatar from "boring-avatars";
import { Pencil } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useActiveAccount } from "thirdweb/react";
import { useFirstEOA } from "../../hooks/useFirstEOA.js";
import { IPFSMediaRenderer } from "../IPFSMediaRenderer/IPFSMediaRenderer.js";
const ProfileSection = () => {
  const {
    authStrategy
  } = useB3Config();
  const isBetterAuth = authStrategy === "better-auth";
  const account = useActiveAccount();
  const {
    address: eoaAddress
  } = useFirstEOA();
  const {
    address: smartWalletAddress
  } = useAccountWallet();
  const {
    data: profile
  } = useProfile({
    address: eoaAddress || account?.address,
    fresh: true
  });
  const {
    user
  } = useUser();
  const setB3ModalOpen = useModalStore(state => state.setB3ModalOpen);
  const setB3ModalContentType = useModalStore(state_0 => state_0.setB3ModalContentType);
  const navigateBack = useModalStore(state_1 => state_1.navigateBack);
  const {
    data: simBalance
  } = useSimBalance(smartWalletAddress);
  const [imgError, setImgError] = useState(false);
  const handleImgError = useCallback(() => setImgError(true), []);
  const totalBalanceUsd = useMemo(() => {
    if (!simBalance?.balances) return 0;
    return simBalance.balances.reduce((sum, token) => sum + (token.value_usd || 0), 0);
  }, [simBalance]);
  const handleEditAvatar = () => {
    setB3ModalOpen(true);
    setB3ModalContentType({
      type: "avatarEditor",
      onSuccess: () => {
        navigateBack();
      }
    });
  };
  const avatarSrc = useMemo(() => validateImageUrl(user?.avatar) || validateImageUrl(profile?.avatar), [user?.avatar, profile?.avatar]);
  // Reset error state when avatar source changes (e.g. user uploads a new avatar)
  useEffect(() => {
    setImgError(false);
  }, [avatarSrc]);
  const currentUsername = user?.username || profile?.displayName || formatUsername(profile?.name || "");
  const avatarSeed = eoaAddress || account?.address || smartWalletAddress || currentUsername || user?.email || "user";
  return _jsx("div", {
    className: "flex items-center justify-between px-5 py-6",
    children: _jsxs("div", {
      className: "global-account-profile flex items-center gap-4",
      children: [_jsxs("div", {
        className: "global-account-profile-avatar relative",
        children: [_jsx("div", {
          className: "border-b3-line border-1 bg-b3-primary-wash size-14 overflow-hidden rounded-full border",
          children: avatarSrc && !imgError ? _jsx(IPFSMediaRenderer, {
            src: avatarSrc,
            alt: "Profile Avatar",
            className: "h-full w-full object-cover",
            onError: handleImgError
          }) : _jsx(Avatar, {
            name: avatarSeed,
            variant: "beam",
            size: 56,
            colors: AVATAR_COLORS
          })
        }), _jsx("button", {
          onClick: handleEditAvatar,
          className: "border-b3-background hover:bg-b3-grey/80 absolute -bottom-1 -right-1 flex size-6 items-center justify-center rounded-full border-4 bg-[#a0a0ab] transition-colors",
          children: _jsx(Pencil, {
            size: 10,
            className: "text-b3-background"
          })
        })]
      }), _jsxs("div", {
        className: "global-account-profile-info flex flex-col gap-1",
        children: [!isBetterAuth && _jsxs("h2", {
          className: "text-b3-grey font-neue-montreal-semibold flex h-[38px] items-center gap-1 text-xl",
          children: [_jsx("div", {
            className: "text-b3-foreground-muted",
            children: " $"
          }), _jsx("div", {
            className: "text-[30px]",
            children: formatDisplayNumber(totalBalanceUsd, {
              fractionDigits: 2
            })
          })]
        }), _jsx("div", {
          className: "b3-modal-username font-neue-montreal-semibold text-base leading-none text-[#0B57C2]",
          children: currentUsername
        }), isBetterAuth && user?.email && _jsx("div", {
          className: "text-b3-foreground-muted font-neue-montreal-medium text-sm",
          children: user.email
        })]
      })]
    })
  });
};
export default ProfileSection;